"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { Drop as DropType, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useMemo } from "react";
import DropContext from "./DropContext";
import { DropLocation } from "./drop.types";
import type { DropIdentityMode, DropInteractionParams, DropTimestampLayout } from "./drop.types";
import ParticipationDrop from "./participation/ParticipationDrop";
import WaveDrop from "./WaveDrop";
import WinnerDrop from "./winner/WinnerDrop";
export type { DropInteractionParams } from "./drop.types";
export { DropLocation } from "./drop.types";
interface DropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: DropType | null;
  readonly nextDrop: DropType | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  readonly wrapContentOnly?: (content: React.ReactNode) => React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function Drop(t0) {
  const $ = _c(76);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    parentContainerRef,
    wrapContentOnly,
    footer,
    identityMode,
    timestampLayout,
    showInteractions: t1,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed: t2,
    isVotingControlsLocked: t3,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const canOpenDrop = drop.drop_type !== ApiDropType.Chat || location !== DropLocation.WAVE;
  const openDropContentClick = canOpenDrop ? onDropContentClick : undefined;
  let t4;
  if ($[0] !== activeDrop || $[1] !== drop || $[2] !== embedDepth || $[3] !== embedPath || $[4] !== footer || $[5] !== identityMode || $[6] !== isVotingClosed || $[7] !== isVotingControlsLocked || $[8] !== location || $[9] !== maxEmbedDepth || $[10] !== onQuoteClick || $[11] !== onReply || $[12] !== openDropContentClick || $[13] !== parentContainerRef || $[14] !== quotePath || $[15] !== showInteractions || $[16] !== showReplyAndQuote || $[17] !== showWaveInfo || $[18] !== timestampLayout || $[19] !== winningThreshold || $[20] !== winningThresholdMinDurationMs) {
    t4 = <ParticipationDrop drop={drop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} onReply={onReply} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[0] = activeDrop;
    $[1] = drop;
    $[2] = embedDepth;
    $[3] = embedPath;
    $[4] = footer;
    $[5] = identityMode;
    $[6] = isVotingClosed;
    $[7] = isVotingControlsLocked;
    $[8] = location;
    $[9] = maxEmbedDepth;
    $[10] = onQuoteClick;
    $[11] = onReply;
    $[12] = openDropContentClick;
    $[13] = parentContainerRef;
    $[14] = quotePath;
    $[15] = showInteractions;
    $[16] = showReplyAndQuote;
    $[17] = showWaveInfo;
    $[18] = timestampLayout;
    $[19] = winningThreshold;
    $[20] = winningThresholdMinDurationMs;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== activeDrop || $[23] !== drop || $[24] !== dropViewDropId || $[25] !== embedDepth || $[26] !== embedPath || $[27] !== footer || $[28] !== identityMode || $[29] !== location || $[30] !== maxEmbedDepth || $[31] !== nextDrop || $[32] !== onQuoteClick || $[33] !== onReply || $[34] !== onReplyClick || $[35] !== openDropContentClick || $[36] !== parentContainerRef || $[37] !== previousDrop || $[38] !== quotePath || $[39] !== showInteractions || $[40] !== showReplyAndQuote || $[41] !== showWaveInfo || $[42] !== timestampLayout) {
    t5 = <WinnerDrop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[22] = activeDrop;
    $[23] = drop;
    $[24] = dropViewDropId;
    $[25] = embedDepth;
    $[26] = embedPath;
    $[27] = footer;
    $[28] = identityMode;
    $[29] = location;
    $[30] = maxEmbedDepth;
    $[31] = nextDrop;
    $[32] = onQuoteClick;
    $[33] = onReply;
    $[34] = onReplyClick;
    $[35] = openDropContentClick;
    $[36] = parentContainerRef;
    $[37] = previousDrop;
    $[38] = quotePath;
    $[39] = showInteractions;
    $[40] = showReplyAndQuote;
    $[41] = showWaveInfo;
    $[42] = timestampLayout;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  const t6 = previousDrop?.type === DropSize.FULL ? previousDrop : null;
  const t7 = nextDrop?.type === DropSize.FULL ? nextDrop : null;
  let t8;
  if ($[44] !== activeDrop || $[45] !== drop || $[46] !== dropViewDropId || $[47] !== embedDepth || $[48] !== embedPath || $[49] !== footer || $[50] !== identityMode || $[51] !== location || $[52] !== maxEmbedDepth || $[53] !== onQuoteClick || $[54] !== onReply || $[55] !== onReplyClick || $[56] !== openDropContentClick || $[57] !== quotePath || $[58] !== showInteractions || $[59] !== showReplyAndQuote || $[60] !== showWaveInfo || $[61] !== t6 || $[62] !== t7 || $[63] !== timestampLayout || $[64] !== wrapContentOnly) {
    t8 = <WaveDrop drop={drop} previousDrop={t6} nextDrop={t7} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} wrapContentOnly={wrapContentOnly} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[44] = activeDrop;
    $[45] = drop;
    $[46] = dropViewDropId;
    $[47] = embedDepth;
    $[48] = embedPath;
    $[49] = footer;
    $[50] = identityMode;
    $[51] = location;
    $[52] = maxEmbedDepth;
    $[53] = onQuoteClick;
    $[54] = onReply;
    $[55] = onReplyClick;
    $[56] = openDropContentClick;
    $[57] = quotePath;
    $[58] = showInteractions;
    $[59] = showReplyAndQuote;
    $[60] = showWaveInfo;
    $[61] = t6;
    $[62] = t7;
    $[63] = timestampLayout;
    $[64] = wrapContentOnly;
    $[65] = t8;
  } else {
    t8 = $[65];
  }
  let t9;
  if ($[66] !== t4 || $[67] !== t5 || $[68] !== t8) {
    t9 = {
      [ApiDropType.Participatory]: t4,
      [ApiDropType.Winner]: t5,
      [ApiDropType.Chat]: t8
    };
    $[66] = t4;
    $[67] = t5;
    $[68] = t8;
    $[69] = t9;
  } else {
    t9 = $[69];
  }
  const components = t9;
  let t10;
  if ($[70] !== drop || $[71] !== location) {
    t10 = {
      drop,
      location
    };
    $[70] = drop;
    $[71] = location;
    $[72] = t10;
  } else {
    t10 = $[72];
  }
  const memoizedValue = t10;
  const t11 = components[drop.drop_type];
  let t12;
  if ($[73] !== memoizedValue || $[74] !== t11) {
    t12 = <DropContext.Provider value={memoizedValue}>{t11}</DropContext.Provider>;
    $[73] = memoizedValue;
    $[74] = t11;
    $[75] = t12;
  } else {
    t12 = $[75];
  }
  return t12;
}